import { QueryClient } from '@tanstack/react-query';

export interface ApiFailureState {
  isOffline: boolean;
  failedEndpoints: Set<string>;
  lastFailureTime: number;
  consecutiveFailures: number;
  recoveryAttempts: number;
}

export interface FallbackConfig {
  maxRetries: number;
  retryDelay: number;
  fallbackThreshold: number;
  recoveryCheckInterval: number;
  criticalEndpoints: string[];
}

export class ApiFailureDetector {
  private state: ApiFailureState = {
    isOffline: false,
    failedEndpoints: new Set(),
    lastFailureTime: 0,
    consecutiveFailures: 0,
    recoveryAttempts: 0,
  };

  private config: FallbackConfig = {
    maxRetries: 3,
    retryDelay: 1000,
    fallbackThreshold: 2,
    recoveryCheckInterval: 30000,
    criticalEndpoints: ['/api/campaigns', '/api/tracking', '/api/analytics'],
  };

  private listeners: Array<(state: ApiFailureState) => void> = [];
  private recoveryTimer: NodeJS.Timeout | null = null;

  constructor(config?: Partial<FallbackConfig>) {
    if (config) {
      this.config = { ...this.config, ...config };
    }

    // Listen to online/offline events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline.bind(this));
      window.addEventListener('offline', this.handleOffline.bind(this));
    }
  }

  public recordFailure(endpoint: string, error: Error): boolean {
    console.warn(`API failure detected for ${endpoint}:`, error);

    this.state.failedEndpoints.add(endpoint);
    this.state.lastFailureTime = Date.now();
    this.state.consecutiveFailures++;

    const shouldFallback = this.shouldActivateFallback(endpoint);
    
    if (shouldFallback && !this.state.isOffline) {
      this.activateFallbackMode();
    }

    this.notifyListeners();
    return shouldFallback;
  }

  public recordSuccess(endpoint: string): void {
    if (this.state.failedEndpoints.has(endpoint)) {
      this.state.failedEndpoints.delete(endpoint);
      
      // If this was the last failed endpoint, consider recovery
      if (this.state.failedEndpoints.size === 0) {
        this.attemptRecovery();
      }
      
      this.notifyListeners();
    }
  }

  public shouldUseFallback(endpoint?: string): boolean {
    if (this.state.isOffline) return true;
    
    if (endpoint && this.state.failedEndpoints.has(endpoint)) {
      return true;
    }

    return this.state.consecutiveFailures >= this.config.fallbackThreshold;
  }

  public getFailureState(): ApiFailureState {
    return { ...this.state };
  }

  public addListener(callback: (state: ApiFailureState) => void): () => void {
    this.listeners.push(callback);
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private shouldActivateFallback(endpoint: string): boolean {
    // Always fallback for critical endpoints
    if (this.config.criticalEndpoints.includes(endpoint)) {
      return true;
    }

    // Fallback if we've hit the threshold
    return this.state.consecutiveFailures >= this.config.fallbackThreshold;
  }

  private activateFallbackMode(): void {
    console.warn('Activating fallback mode due to API failures');
    this.state.isOffline = true;
    this.startRecoveryTimer();
  }

  private attemptRecovery(): void {
    if (!this.state.isOffline) return;

    console.info('Attempting API recovery...');
    this.state.recoveryAttempts++;
    this.state.consecutiveFailures = 0;
    this.state.isOffline = false;

    if (this.recoveryTimer) {
      clearInterval(this.recoveryTimer);
      this.recoveryTimer = null;
    }

    this.notifyListeners();
  }

  private startRecoveryTimer(): void {
    if (this.recoveryTimer) return;

    this.recoveryTimer = setInterval(() => {
      // Try a lightweight health check
      this.performHealthCheck();
    }, this.config.recoveryCheckInterval);
  }

  private async performHealthCheck(): Promise<void> {
    try {
      // Simple fetch to check connectivity
      const response = await fetch('/api/health', {
        method: 'HEAD',
        cache: 'no-cache',
      });

      if (response.ok) {
        this.attemptRecovery();
      }
    } catch (error) {
      console.debug('Health check failed, remaining in fallback mode');
    }
  }

  private handleOnline(): void {
    console.info('Network connection restored');
    this.attemptRecovery();
  }

  private handleOffline(): void {
    console.warn('Network connection lost');
    this.state.isOffline = true;
    this.notifyListeners();
  }

  private notifyListeners(): void {
    this.listeners.forEach(callback => {
      try {
        callback(this.state);
      } catch (error) {
        console.error('Error in failure state listener:', error);
      }
    });
  }

  public cleanup(): void {
    if (this.recoveryTimer) {
      clearInterval(this.recoveryTimer);
      this.recoveryTimer = null;
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline.bind(this));
      window.removeEventListener('offline', this.handleOffline.bind(this));
    }

    this.listeners = [];
  }
}

// Global instance
export const apiFailureDetector = new ApiFailureDetector();

// React Query integration
export const createFailureAwareQueryClient = (): QueryClient => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          const endpoint = (error as any)?.config?.url || 'unknown';
          
          if (apiFailureDetector.shouldUseFallback(endpoint)) {
            return false; // Don't retry, use fallback
          }

          return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: (failureCount, error) => {
          const endpoint = (error as any)?.config?.url || 'unknown';
          
          if (apiFailureDetector.shouldUseFallback(endpoint)) {
            return false;
          }

          return failureCount < 2;
        },
      },
    },
  });
};